<template>
  <va-modal
    v-model="state.isOpen"
    hide-default-actions
    :title="$t('assignments.createAssignmentModal.title')"
  >
    <!-- Form for creating a new assignment -->
    <va-form @submit.prevent="createAssignment">
      <!-- Email input field (required) -->
      <va-input
        v-model="state.email"
        :label="$t('assignments.createAssignmentModal.email')"
        :rules="[v => !!v || $t('validation.required')]"
        class="email-input"
      />
      
      <!-- Allowance input field (optional, only shown if hasUsageQuota is true) -->
      <va-input
        v-if="hasUsageQuota"
        v-model="state.allowance"
        type="number"
        :label="$t('assignments.createAssignmentModal.allowance')"
        :rules="[v => validateAllowance(Number(v))]"
        class="allowance-input"
      />
    </va-form>

    <!-- Modal footer with action buttons -->
    <template #footer>
      <div class="modal-footer">
        <va-button class="mr-2" @click="emit('cancel')">
          {{ $t('buttons.cancel') }}
        </va-button>
        <va-button 
          type="submit" 
          :loading="state.loading" 
          @click="createAssignment"
        >
          {{ $t('buttons.create') }}
        </va-button>
      </div>
    </template>
  </va-modal>
</template>

<script setup>
import { reactive, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { callCreateAssignment } from '@/api/licensing';
import { useStore } from 'vuex';

/**
 * Props definition
 * @property {boolean} isOpen - Controls modal visibility
 * @property {string} entitlementId - ID of the entitlement to assign
 */
const props = defineProps({
  isOpen: Boolean,
  entitlementId: String
});

// Store setup
const store = useStore();

// Computed property for hasUsageQuota
const hasUsageQuota = computed(() => {
  const entitlement = store.state.licensing.entitlementEdit;
  return entitlement?.usage?.type === 'Credits';
});

// Event emitter and i18n setup
const emit = defineEmits(['cancel', 'created']);
const { t } = useI18n();

// Inject global services
const toasts = inject('toasts');
const handleError = inject('handleError');

/**
 * Component state
 * @property {boolean} isOpen - Computed property for modal visibility
 * @property {string} email - User email input
 * @property {number} allowance - Usage allowance input
 * @property {boolean} loading - Loading state for form submission
 */
const state = reactive({
  isOpen: computed(() => props.isOpen),
  email: '',
  allowance: 0,
  loading: false
});

/**
 * Validates the allowance value
 * @param {number} value - The allowance value to validate
 * @returns {boolean|string} True if valid, error message if invalid
 */
const validateAllowance = (value) => {
  if (!hasUsageQuota.value) {
    return true;
  }
  return value >= 0 || t('validation.positiveNumber');
};

/**
 * Creates a new assignment
 * Handles form submission, API call, and success/error states
 */
const createAssignment = async () => {
  state.loading = true;
  try {
    const data = {
      entitlementId: props.entitlementId,
      email: state.email,
      origin: 'Customer'
    };

    if (hasUsageQuota.value) {
      data.usageQuota = {
        allowance: Number(state.allowance),
        remainingAllowance: Number(state.allowance)
      };
    }

    // Call API with the correct payload structure
    await callCreateAssignment({ data });
    toasts?.info(t('assignments.createAssignmentModal.success'));
    emit('created');
    emit('cancel');
  } catch (error) {
    handleError(error?.response?.data || error);
    toasts?.error(t('assignments.createAssignmentModal.error'));
  } finally {
    state.loading = false;
  }
};
</script>

<style scoped>
.email-input {
  width: 20rem;
  margin-bottom: 1.5rem;
  font-size: 1.1em;
}

.allowance-input {
  width: 20rem;
  margin-bottom: 1.5rem;
  font-size: 1.1em;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
}
</style> 